
import { useState } from 'react';
import { Package, Building, CheckCircle, Shield, Users, Leaf, Settings } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Product } from '@/types/portfolio';
import { useNavigate } from 'react-router-dom';

interface VerificationScorecardProps {
  product: Product;
}

export const VerificationScorecard = ({ product }: VerificationScorecardProps) => {
  const [selectedCertification, setSelectedCertification] = useState<string>('none');
  const navigate = useNavigate();

  const scorecardCategories = [
    { 
      name: "Chemical Safety", 
      icon: <Shield className="h-5 w-5" />,
      verified: product.certification_status === "certified",
      description: "Safe ingredients and formulation"
    },
    { 
      name: "Packaging", 
      icon: <Package className="h-5 w-5" />,
      verified: product.certification_status === "certified",
      description: "Sustainable packaging materials"
    },
    { 
      name: "Label", 
      icon: <CheckCircle className="h-5 w-5" />,
      verified: product.certification_status === "certified",
      description: "Accurate and compliant labeling"
    },
    { 
      name: "Manufacturing", 
      icon: <Building className="h-5 w-5" />,
      verified: product.certification_status === "certified",
      description: "Responsible manufacturing processes"
    },
    { 
      name: "Performance Claims", 
      icon: <CheckCircle className="h-5 w-5" />,
      verified: product.certification_status === "certified",
      description: "Verified product effectiveness"
    }
  ];

  const certificationOptions = [
    { value: 'none', label: 'None' },
    { value: 'green-seal', label: 'Green Seal' },
    { value: 'epa-safer-choice', label: 'EPA Safer Choice' },
    { value: 'cradle-to-cradle', label: 'Cradle to Cradle' }
  ];

  const handleManageCertification = () => {
    navigate(`/client/portfolios/${product.portfolio_id}/products/${product.id}/certification`, {
      state: { certification: selectedCertification }
    });
  };

  const renderScorecard = () => {
    if (product.certification_status === "certified") {
      // Show verified status for certified products
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {scorecardCategories.map((category, index) => (
            <div key={index} className="text-center">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
                category.verified ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
              }`}>
                {category.icon}
              </div>
              <h3 className="font-medium text-sm mb-1">{category.name}</h3>
              <p className="text-xs text-muted-foreground mb-2">{category.description}</p>
              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                category.verified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}>
                <CheckCircle className="h-3 w-3" />
                {category.verified ? 'Verified' : 'Pending'}
              </div>
              
              {/* Special case for Chemical Safety - show safety icons */}
              {category.name === "Chemical Safety" && category.verified && (
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className="flex items-center text-green-600" title="Safe for humans">
                    <Users className="h-3 w-3" />
                  </div>
                  <div className="flex items-center text-green-600" title="Safe for environment">
                    <Leaf className="h-3 w-3" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // For non-certified products, show certification selector
    if (selectedCertification === 'none') {
      return (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No verification system selected</p>
          <p className="text-sm text-muted-foreground mt-2">
            Choose a certification system above to see the verification requirements
          </p>
        </div>
      );
    }

    // Show pending status for selected certification
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {scorecardCategories.map((category, index) => (
          <div key={index} className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 bg-gray-100 text-gray-400">
              {category.icon}
            </div>
            <h3 className="font-medium text-sm mb-1">{category.name}</h3>
            <p className="text-xs text-muted-foreground mb-2">{category.description}</p>
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <CheckCircle className="h-3 w-3" />
              Pending
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Verification Scorecard</CardTitle>
        <CardDescription>
          {product.certification_status === "certified" 
            ? "Assessment across 5 major certification categories"
            : "Choose a certification system to begin verification process"
          }
        </CardDescription>
        
        {/* Show certification selector for non-certified products */}
        {product.certification_status !== "certified" && (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">
              Select Certification System:
            </label>
            <Select value={selectedCertification} onValueChange={setSelectedCertification}>
              <SelectTrigger className="w-full max-w-sm">
                <SelectValue placeholder="Choose certification system" />
              </SelectTrigger>
              <SelectContent>
                {certificationOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {renderScorecard()}
        
        {/* Show Manage Certification button when a certification system is selected (not 'none') */}
        {product.certification_status !== "certified" && selectedCertification !== 'none' && (
          <div className="flex justify-center mt-6">
            <Button onClick={handleManageCertification} className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Manage Certification
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
